import { Race, Sail } from './Race.js';
import { Info } from './Info.js';
import { Polar } from './Polar.js';
import { Settings } from './Settings.js';
import * as gd from '../utils/drive-helpers.js';
import { compressStringToGzip, decompressGzipToString } from '../utils/compress-util.js';

function getMimeType(fileName) {
  if (!fileName) return 'application/json';
  const ext = fileName.toLowerCase().split('.').pop();
  const mimeTypes = {
    'json': 'application/json',
    'gz': 'application/gzip',
    'txt': 'text/plain'
  };
  return mimeTypes[ext] || 'application/json';
}

function shouldCompress(fileName) {
  return fileName?.toLowerCase().endsWith('.gz');
}

function decompressIfNeeded(data, fileName) {
  if (shouldCompress(fileName)) {
    return decompressGzipToString(data);
  }
  return typeof data === 'string' ? data : new TextDecoder().decode(data);
}

function compressIfNeeded(text, fileName) {
  return shouldCompress(fileName) ? compressStringToGzip(text) : text;
}

/** 
 * DataStore is the interface used to isolate the application from the file storage technology. 
 * Currently it is google drive but may change in future.
*/

export class DataStore {
  constructor() {
    this.raceData = null;
    this.sharedFileParams = null;
    this.settings = null;
  }

  getSettings() {
    return this.settings;
  }

  getRace() {
    return this.raceData;
  }

  reset() {
    this.raceData = null;
  }

  setSharedFileParams(data) {
    this.sharedFileParams = data;
  }

  getSharedFileParams() {
    return this.sharedFileParams;
  }

  isSharedFileMode() {
    return this.sharedFileParams != null;
  }

  async loadFromSharedFile(notifyFn) {
    let result = null;
    await notifyFn('Rigging the boat...');
    if (this.sharedFileParams.id != null) {
      result = await gd.fetchSharedFileContentGDrive(this.sharedFileParams.id);
    } else if (this.sharedFileParams.url != null) {
      result = await gd.fetchSharedFileContentURLAsync(this.sharedFileParams.url);
    }

    if (this.sharedFileParams.sid != null) {
      await notifyFn('Stowing gear...');
      const settingsResult = await gd.fetchSharedFileContentGDrive(this.sharedFileParams.sid);
      const settingsText = decompressIfNeeded(settingsResult.data, settingsResult.fileName);
      const settingsJson = JSON.parse(settingsText);
      this.settings = new Settings(settingsJson, this.sharedFileParams.sid);
    } else {
      this.settings = new Settings(Settings.createDefault());
    }

    let info = new Info();
    if (this.sharedFileParams.iid != null) {
      await notifyFn('Settling in...');
      const infoResult = await gd.fetchSharedFileContentGDrive(this.sharedFileParams.iid);
      const infoText = decompressIfNeeded(infoResult.data, infoResult.fileName);
      console.log(infoText);
      info = Info.loadFromJSON(infoText, infoResult.fileName, this.sharedFileParams.iid);
    }

    await notifyFn('Packing the kite...');
    const text = decompressIfNeeded(result.data, result.fileName);
    const sail = await Sail.loadFromJSON(text, result.fileName, null, this.settings, notifyFn);
    this.raceData = new Race(sail, info);
  }

  async loadFromGDrive(doc, notifyFn) {
    await notifyFn('Rigging the boat...');
    const data = await gd.fetchFileContentAsync(doc.id);
    await notifyFn('Packing the kite...');
    const decompressed = decompressIfNeeded(data, doc.name);
    const fileNameLower = doc.name.toLowerCase();
    if (fileNameLower.indexOf(".json") !== -1) {
      const sail = await Sail.loadFromJSON(decompressed, doc.name, doc.id, this.settings, notifyFn);
      await notifyFn('Settling in...');
      const raceInfoFileName = Info.infoFileNameFor(doc.name);
      const result = await gd.downloadNamedFileFromDataFolderAsync(await gd.ensureAppDataFolderAsync(), raceInfoFileName);
      let info = new Info(null, raceInfoFileName, null);
      if (result) {
        const infoText = decompressIfNeeded(result.data, raceInfoFileName);
        info = Info.loadFromJSON(infoText, raceInfoFileName, result.id);
      }
      this.raceData = new Race(sail, info);
    } else if (fileNameLower.indexOf(".txt") !== -1) {
      await notifyFn('Cleaning the hull...');
      //this.raceData = await Race.loadFromNMEA(decompressed, doc.name, notifyFn, this.settings);
      await this.loadFromNMEA(decompressed, doc.name, notifyFn);
    }
  }

  
  static async loadSailFromGDriveById(fileId, fileName) {
    const data = await gd.fetchFileContentAsync(fileId);
    const text = decompressIfNeeded(data, fileName);
    return await Sail.loadFromJSON(text, fileName, fileId);
  }

  static async loadInfoFromGDriveById(fileId, fileName) {
    const data = await gd.fetchFileContentAsync(fileId);
    const text = decompressIfNeeded(data, fileName);
    return Info.loadFromJSON(text, fileName, fileId);
  }

  // no one uses this right now
  static async loadPolarFromGDriveById(fileId, fileName) {
    const data = await gd.fetchFileContentAsync(fileId);
    const text = decompressIfNeeded(data, fileName);
    return Polar.loadFromJSON(text, fileName, fileId);
  }

  static async loadPolarByNameAsync(fileName) {
    const folder = await gd.ensurePolarFolderAsync();
    const result = await gd.downloadNamedFileFromDataFolderAsync(folder, fileName);
    const text = decompressIfNeeded(result.data, fileName);
    if (result) {
      return Polar.loadFromJSON(text, fileName, result.id);
    }
    return null;
  }

  /**
   * Generates a shareable link for a Google Drive file
   * @param {string} fileId - Google Drive file ID for the race
   * @param {string} settingsFileId - Google Drive file ID for settings (optional)
   * @returns {string} - Complete shareable URL
   */
  static generateShareableLink(fileId, settingsFileId = null, infoFileId = null) {
    let url = `${window.location.origin}${window.location.pathname}?id=${fileId}`;
    if (settingsFileId) {
      url += `&sid=${settingsFileId}`;
    }
    if (infoFileId) {
      url += `&iid=${infoFileId}`;
    }
    return url;
  }

  /**
   * Shares the current race file and generates a shareable link
   * @param {Function} setLoadingMessage - Callback for loading status updates
   * @returns {Promise<{success: boolean, link?: string, error?: string, errorType?: string}>}
   */
  async shareCurrentRace(setLoadingMessage) {
    try {
      if (!this.raceData || !this.raceData.sail() || !this.raceData.sail().fileId) {
        return {
          success: false,
          error: 'Cannot share: file not found in Google Drive.',
          errorType: 'validation'
        };
      }

      const fileId = this.raceData.sail().fileId;
      const settingsFileId = this.settings.getFileId();
      const infoFileId = this.raceData.info()?.fileId ?? null;

      await setLoadingMessage('Checking file permissions...');
      const raceAlreadyShared = await gd.isFileShared(fileId);

      if (!raceAlreadyShared) {
        await setLoadingMessage('Sharing race file...');
        await gd.createPublicReadPermission(fileId);
      }

      if (settingsFileId) {
        const settingsAlreadyShared = await gd.isFileShared(settingsFileId);
        if (!settingsAlreadyShared) {
          await setLoadingMessage('Sharing settings...');
          await gd.createPublicReadPermission(settingsFileId);
        }
      }

      if (infoFileId) {
        const infoAlreadyShared = await gd.isFileShared(infoFileId);
        if (!infoAlreadyShared) {
          await setLoadingMessage('Sharing course...');
          await gd.createPublicReadPermission(infoFileId);
        }
      }

      const link = DataStore.generateShareableLink(fileId, settingsFileId, infoFileId);

      return {
        success: true,
        link: link
      };

    } catch (error) {
      console.error('Share operation failed:', error);

      let errorType = 'unknown';
      let errorMessage = 'An unexpected error occurred. Please try again.';

      const errorString = error.message || error.toString();

      if (errorString.includes('401') || errorString.includes('Unauthorized') ||
          errorString.includes('invalid') || errorString.includes('expired')) {
        errorType = 'auth';
        errorMessage = 'Please log in to Google Drive to share files';
      } else if (errorString.includes('403') || errorString.includes('permission')) {
        errorType = 'permission';
        errorMessage = 'Unable to share file. Please check your Google Drive permissions.';
      } else if (errorString.includes('Network') || errorString.includes('fetch') ||
                 errorString.includes('Failed to fetch')) {
        errorType = 'network';
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      return {
        success: false,
        error: errorMessage,
        errorType: errorType
      };
    }
  }


  async ensurePolarLoadedAsync() {
    const fileName = this.settings.getDefaultPolarFile();
    if (fileName == null) return;
    let polar = this.settings.getDefaultPolarCache();
    if (polar == null) {
      polar = await DataStore.loadPolarByNameAsync(fileName);
      if (!polar) {
        throw new Error(`Could not load polar file: ${fileName}`);
      }
      this.settings.setDefaultPolarCache(polar);
    }
    if (this.raceData?.sail() != null) {
      this.raceData.sail().calculatePolarPerformance(polar);
    }
  }

  async saveDefaultPolarAsync(fileName) {
    this.settings.setDefaultPolarFile(fileName);
    await this.saveSettingsAsync();
    this.settings.clearDefaultPolarCache();
  }

  //TODO should be static, need to review use
  async savePolarToGDrive(polar, setLoadingMessage) {
    const fileName = polar.fileName;
    const content = polar.serialiseToJSON();
    const mimeType = getMimeType(fileName);
    await setLoadingMessage('Uploading...');
    if (!polar.fileId) {
      const fileId = await gd.uploadFileToGDriveAsync(fileName, mimeType, content, await gd.ensurePolarFolderAsync());
      polar.fileId = fileId;
    } else {
      await gd.updateFileContentAsync(polar.fileId, content, mimeType);
    }
  }

  _trimAisTracks() {
    const mmsiArray = this.raceData.sail().getTracks()
      .filter(t => t.getMMSI() != null && t.isVisible())
      .map(t => t.getMMSI());
    this.raceData.sail().keepTheseAisTracks(mmsiArray);
  }

  async saveToGDrive(setLoadingMessage) {
    if (!this.raceData.sail().hasFileId()) {
      throw new Error("Can't save file, no id on Google Drive");
    }
    // this._trimAisTracks(); //don't prune AIS tracks on normal save
    const fileName = this.raceData.sail().fileName;
    await setLoadingMessage('Packing the kite...');
    const content = compressIfNeeded(this.raceData.sail().serialiseToJSON(), fileName);
    const mimeType = getMimeType(fileName);
    await setLoadingMessage('Uploading...');
    await gd.updateFileContentAsync(this.raceData.sail().fileId, content, mimeType);
  }

  async saveAsToGDrive(fileName, setLoadingMessage) {
    this._trimAisTracks();
    this.raceData.setFileNames(fileName);
    await setLoadingMessage('Packing the kite...');
    const content = compressIfNeeded(this.raceData.sail().serialiseToJSON(), fileName);
    const mimeType = getMimeType(fileName);
    await setLoadingMessage('Uploading...');
    const fileId = await gd.uploadFileToGDriveAsync(fileName, mimeType, content, await gd.ensureDataFolderAsync());
    this.raceData.sail().setFileId(fileId);
    return fileId;
  }

  async saveRaceInfoToGDrive(setLoadingMessage) {
    if (this._savingRaceInfo) return;
    this._savingRaceInfo = true;
    try {
      const fileName = this.raceData.info().fileName;
      const mimeType = getMimeType(fileName);
      const content = this.raceData.info().serialiseToJSON();
      await setLoadingMessage('Uploading...');
      if (!this.raceData.info().fileId) {
        const fileId = await gd.uploadFileToGDriveAsync(fileName, mimeType, content, await gd.ensureAppDataFolderAsync());
        this.raceData.info().setFileId(fileId);
      } else {
        await gd.updateFileContentAsync(this.raceData.info().fileId, content, mimeType);
      }
    } finally {
      this._savingRaceInfo = false;
    }
  }

  async saveNMEAToGDrive(text, name, setLoadingMessage) {
    const folder = await gd.ensureTrackDataFolderAsync();
    await setLoadingMessage('Compressing...');
    const content = compressIfNeeded(text, name);
    const mimeType = getMimeType(name);
    await setLoadingMessage('Uploading...');
    await gd.uploadFileToGDriveAsync(name, mimeType, content, folder);
  }

  async pickAndLoadFromGDrive(callbackOk, callbackErr, setLoadingMessage) {
    await gd.drivePickAndDownloadFileAsync(await gd.ensureDataFolderAsync(), async (response, doc) => {
      if (response.ok) {
        await this.loadFromGDrive(doc, setLoadingMessage);
        callbackOk(doc);
      } else {
        callbackErr(response)
      }
    });
  }

  async loadFromNMEA(text, fileName, notifyFn) {
    this.raceData = await Race.loadFromNMEA(text, fileName, notifyFn, this.settings);
  }

  static extractRaceFiles(folderList) {
    const result = [];
    for (const item of folderList) {
        if ((item.mimeType == "application/json" && item.name.endsWith('.json'))
          || (item.mimeType == "application/gzip" && item.name.endsWith('.json.gz'))) {
            const raceName = item.name.replace(/\.json(\.gz)?$/, '').replace(/__info\./, '');
            let fileRec = result.find(x => x.raceName == raceName);
            if (fileRec == null) {
                fileRec = { raceName: raceName };
                result.push(fileRec);
            }
            if (item.name.includes('__info')) {
                fileRec.infoFile = item;
            } else {
                fileRec.raceFile = item;
            }
        }
    }
    for (let i = result.length - 1; i >= 0; i--) {
        if (result[i].infoFile == undefined || result[i].raceFile == undefined) {
            result.splice(i, 1);
        }
    }
    return result;
  }

  async loadSettingsAsync() {
    const folder = await gd.ensureAppDataFolderAsync();
    const result = await gd.downloadNamedFileFromDataFolderAsync(folder, 'settings.json');
    let settingsJson;
    if (result) {
      const text = decompressIfNeeded(result.data, 'settings.json');
      settingsJson = JSON.parse(text);
    } else {
      settingsJson = Settings.createDefault();
    }
    this.settings = new Settings(settingsJson, result?.id ?? null);
  }

  async saveSettingsAsync() {
    const folder = await gd.ensureAppDataFolderAsync();
    const content = this.settings.toJSON();
    const settingsFileId = this.settings.getFileId();

    if (settingsFileId) {
      await gd.updateFileContentAsync(settingsFileId, content, 'application/json');
    } else {
      const newId = await gd.uploadFileToGDriveAsync('settings.json', 'application/json', content, folder);
      this.settings.setFileId(newId);
    }
    
    if (this.raceData?.sail()) {
      this.raceData.sail().calculateFields(this.settings);
    }
  }

}
